import { ChangeDetectionStrategy, Component, ContentChildren, Directive, EventEmitter, Input, OnDestroy, Output, QueryList, HostListener,
    ViewEncapsulation, forwardRef, ChangeDetectorRef, AfterViewInit, ViewChildren, ViewContainerRef, ViewChild, ElementRef,
} from '@angular/core';
import { animate, AnimationBuilder, AnimationFactory, AnimationPlayer, style } from '@angular/animations';
import { Subject, Subscription } from 'rxjs';
import { filter, startWith, takeUntil } from 'rxjs/operators';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { NavigationEnd, Router } from '@angular/router';

import { EuiTabComponent } from './eui-tab/eui-tab.component';

@Component({
    selector: 'eui-tabs',
    templateUrl: './eui-tabs.component.html',
    styleUrls: ['./styles/_index.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class EuiTabsComponent implements OnDestroy, AfterViewInit {
    @Input() styleClass: string;
    @Input() tabs: EuiTabComponent[] = [];
    @Input()
    get isMainNavigation() { return this._isMainNavigation; }
    set isMainNavigation(value) { this._isMainNavigation = coerceBooleanProperty(value); }
    _isMainNavigation = false;
    @Input()
    get isSubTabs() { return this._isSubTabs; }
    set isSubTabs(value) { this._isSubTabs = coerceBooleanProperty(value); }
    _isSubTabs = false;
    @Input() activeTabIndex = null;
    @Input()
    get isVerticalTabs() { return this._isVerticalTabs; }
    set isVerticalTabs(value) { this._isVerticalTabs = coerceBooleanProperty(value); }
    _isVerticalTabs = false;
    @Input() e2eAttr = 'eui-tabs';

    @Output() tabClosed = new EventEmitter<{ tab: EuiTabComponent, index: number }>();
    @Output() tabSelected = new EventEmitter<{ tab: EuiTabComponent, index: number }>();

    @ContentChildren(forwardRef(() => EuiTabComponent), { descendants: false }) _tabs: QueryList<EuiTabComponent>;
    @ViewChildren('euiTabLabelItem', { read: ViewContainerRef }) euiTabLabelItems: QueryList<ViewContainerRef>;
    @ViewChild('euiTabsItems') euiTabsItems: ElementRef;
    @ViewChild('euiTabsItemsWrapper') euiTabsItemsWrapper: ElementRef;
    @ViewChild('host') host: ElementRef;

    public scrolling = false;
    public navigationLeftButtonDisabled = true;
    public navigationRightButtonDisabled = false;
    public currentState = 'initial';
    public currentOffset = 0;

    private destroy$: Subject<boolean> = new Subject<boolean>();
    private routerSubscription: Subscription;
    private player: AnimationPlayer;
    private defaultOffset = 46;
    private observer: any;

    constructor(private cd: ChangeDetectorRef,
                private router: Router,
                private builder: AnimationBuilder) {}

    ngAfterViewInit(): void {
        this.observer = new (window as any).ResizeObserver(entries => {
            this.getScrollingStatus();
            this.cd.detectChanges();
        });

        this.observer.observe(this.host.nativeElement);

        this._tabs.changes.pipe(startWith(true), takeUntil(this.destroy$)).subscribe(() => {
            setTimeout(() => {
                this.tabs = this._tabs.toArray();

                if (this.routerSubscription) {
                    this.routerSubscription.unsubscribe();
                }

                this.routerSubscription = this.router.events.pipe(
                    filter(e => e instanceof NavigationEnd),
                    takeUntil(this.destroy$),
                    startWith({ url: this.router.url }),
                ).subscribe((e: NavigationEnd) => {
                    const tabsWithUrl = this.tabs.filter(t => t.url !== null);
                    if (tabsWithUrl.length === this.tabs.length) {
                        const isUrlInTabsIndex = tabsWithUrl.findIndex(t => e.url.indexOf(t.url) !== -1);
                        this.tabs = this.tabs.map((t, i) => isUrlInTabsIndex === i ?
                            Object.assign(t, { isActive: true }) :
                            Object.assign(t, { isActive: false }));

                        if (this.tabs.findIndex(t => t.isActive) !== -1) {
                            this.activeTabIndex = this.tabs.findIndex(t => t.isActive);
                        }
                        this.cd.detectChanges();
                    }
                });

                if (!this.activeTabIndex) {
                    this.activeTabIndex = this.tabs.findIndex(t => t.isActive) !== -1 ? this.tabs.findIndex(t => t.isActive) : 0;
                }

                this.cd.detectChanges();
            });
        });

        this.getScrollingStatus();
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
        this.observer.unobserve(this.host.nativeElement);
    }

    @HostListener('window:resize', ['$event'])
    onResize(): void {
        this.getScrollingStatus();
    }

    public onContentChanges(): void {
        this.getScrollingStatus();
    }

    public goToLeft(): void {
        const containerWidth = this.euiTabsItems.nativeElement.clientWidth;
        const wrapperWidth = this.euiTabsItemsWrapper.nativeElement.clientWidth;

        const step = this.defaultOffset * 10;
        let newOffset = this.currentOffset + step;
        if (newOffset > 0) {
            newOffset = 0;
        }

        const myAnimation: AnimationFactory = this.builder.build([
            animate('500ms cubic-bezier(0.35, 0, 0.25, 1)', style({ 'margin-left': newOffset + 'px' })),
        ]);

        this.player = myAnimation.create(this.euiTabsItemsWrapper.nativeElement);
        this.player.play();

        this.currentOffset = newOffset;
        if (this.currentOffset === 0) {
            this.navigationLeftButtonDisabled = true;
        }
        if (Math.abs(this.currentOffset) < (wrapperWidth - containerWidth + this.defaultOffset)) {
            this.navigationRightButtonDisabled = false;
        }
    }

    public goToRight(): void {
        const containerWidth = this.euiTabsItems.nativeElement.clientWidth;
        const wrapperWidth = this.euiTabsItemsWrapper.nativeElement.clientWidth;

        const step = this.defaultOffset * 10;
        const maxMove = wrapperWidth - containerWidth + this.defaultOffset;
        let newOffset = this.currentOffset - step;
        if (Math.abs(newOffset) > maxMove) {
            newOffset = -(wrapperWidth - containerWidth + this.defaultOffset);
        }

        const myAnimation: AnimationFactory = this.builder.build([
            animate('500ms cubic-bezier(0.35, 0, 0.25, 1)', style({ 'margin-left': newOffset + 'px' })),
        ]);

        this.player = myAnimation.create(this.euiTabsItemsWrapper.nativeElement);
        this.player.play();

        this.currentOffset = newOffset;
        if (this.currentOffset < 0) {
            this.navigationLeftButtonDisabled = false;
        }
        if (Math.abs(this.currentOffset) === maxMove) {
            this.navigationRightButtonDisabled = true;
        }
    }

    public changeTab(index: number): void {
        if (this.tabs[index] && !this.tabs[index].isDisabled) {

            this.tabs = this.tabs.map((t, i) => i === index ? Object.assign(t, { isActive: true }) : Object.assign(t, { isActive: false }));

            if (!this.tabs[index].url) {
                this.activeTabIndex = index;
                this.tabSelected.emit({ tab: this.tabs[index], index });
            } else {
                this.activeTabIndex = index;
                this.router.navigateByUrl(this.tabs[index].url);
            }

            this.cd.detectChanges();
        }
    }

    public onKeyUp(evt: KeyboardEvent, index: number): void {
        if (evt.code === 'Enter' || evt.code === 'Space') {
            if (this.tabs[index] && !this.tabs[index].isDisabled) {

                this.tabs.map((t, i) => i === index ? Object.assign(t, { isActive: true }) : Object.assign(t, { isActive: false }));

                if (!this.tabs[index].url) {
                    this.activeTabIndex = index;
                    this.tabSelected.emit({ tab: this.tabs[index], index });
                } else {
                    this.activeTabIndex = index;
                    this.router.navigateByUrl(this.tabs[index].url);
                }
            }
        }
    }

    public closeTab(index: number, e: any): void {
        const visibleTabs = this.tabs.filter(t => t.isVisible && !t.isDisabled);
        const closedVisibleTabIndex = visibleTabs.indexOf(this.tabs[index]);

        if (index === this.activeTabIndex) {
            const previousVisibleIndex = this.tabs.indexOf(visibleTabs[closedVisibleTabIndex - 1]);
            const nextVisibleIndex = this.tabs.indexOf(visibleTabs[closedVisibleTabIndex + 1]);

            if (previousVisibleIndex < 0) {
                this.changeTab(nextVisibleIndex);
            } else {
                this.changeTab(previousVisibleIndex);
            }
        }

        this.tabs = this.tabs.map((t, i) => i !== index ? t : Object.assign(t, { isVisible: false }));
        this.tabClosed.emit({ tab: this.tabs[index], index });
        this.cd.detectChanges();

        e.stopPropagation();
    }

    public detectChanges() {
        this.cd.detectChanges();
    }

    private getScrollingStatus(): void {
        const containerWidth = this.euiTabsItems.nativeElement.clientWidth;
        const wrapperWidth = this.euiTabsItemsWrapper.nativeElement.clientWidth;

        this.scrolling = containerWidth < wrapperWidth;

        const maxMove = wrapperWidth - containerWidth + this.defaultOffset;
        if (Math.abs(this.currentOffset) < maxMove) {
            this.navigationRightButtonDisabled = false;
            this.cd.detectChanges();
        }
    }
}

/* tslint:disable */
@Directive({ selector: 'euiTabsRightContent' })
export class EuiTabsRightContentTagDirective {}
/* tslint:enable */
